import { Inject, Injectable } from "@angular/core";
import { CookieService } from "src/app/services/cookie.service";
import { WINDOW } from "src/app/modules/shared/components/feature-branch-banner/feature-branch-cookie.service";
import { parseFeatureEnvCookie, removeFeatureEnv, serializeFeatureEnvs, upsertFeatureEnv } from "./feature-env-cookie";
import { recordRecentFeatureEnv } from "./feature-env-recents";
import {
  FEATURE_ENV_COOKIE_DOMAIN,
  FEATURE_ENV_COOKIE_MAX_AGE_SECONDS,
  FEATURE_ENV_COOKIE_NAME,
  FEATURE_ENV_QUERY_PARAM,
  FeatureEnv,
} from "./feature-env.model";

/**
 * Reads and writes the `features` cookie that routes traffic to feature-branch
 * pods. Applying an environment does not reload — callers decide when, because
 * the deep link and the dev panel reload at different moments.
 */
@Injectable({ providedIn: "root" })
export class FeatureEnvService {
  constructor(
    private readonly cookieService: CookieService,
    @Inject(WINDOW) private readonly win: Window,
  ) {}

  active(): FeatureEnv[] {
    return parseFeatureEnvCookie(this.cookieService.getCookie(FEATURE_ENV_COOKIE_NAME) as string | undefined);
  }

  branchFor(repo: string): string | undefined {
    return this.active().find((env) => env.repo === repo)?.branch;
  }

  apply(repo: string, branch: string): void {
    this.write(upsertFeatureEnv(this.active(), repo, branch));
    recordRecentFeatureEnv({ repo, branch });
  }

  /** Removes one repo's environment, or all of them when no repo is given. */
  clear(repo?: string): void {
    this.write(repo === undefined ? [] : removeFeatureEnv(this.active(), repo));
  }

  /** A link that puts someone else in the environments currently active. */
  shareLink(): string | undefined {
    const value = serializeFeatureEnvs(this.active());
    if (!value) {
      return undefined;
    }
    const url = new URL(this.win.location.pathname, this.win.location.origin);
    url.searchParams.set(FEATURE_ENV_QUERY_PARAM, value);
    return url.toString();
  }

  reload(): void {
    this.win.location.reload();
  }

  private write(envs: readonly FeatureEnv[]): void {
    const value = serializeFeatureEnvs(envs);
    if (!value) {
      // An empty cookie value would still be a cookie; the router must see none.
      this.cookieService.deleteCookie(FEATURE_ENV_COOKIE_NAME, FEATURE_ENV_COOKIE_DOMAIN);
      return;
    }
    this.cookieService.setCookie(
      FEATURE_ENV_COOKIE_NAME,
      value,
      FEATURE_ENV_COOKIE_MAX_AGE_SECONDS,
      FEATURE_ENV_COOKIE_DOMAIN,
    );
  }
}
